// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
// import { updateTenantManagement } from 'apis/tenants';
import { BottomField } from 'components/molecules/BottomField';
import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ProcessingMaster.module.scss';
import { EditableTableCellValue } from 'components/atoms/EditableTableCell';
import { useScrollToFirstError } from 'util/hooks/useScrollToFirstError';
import { SelectField, SelectOption } from 'components/molecules/SelectField';
import { Form, FormRenderProps } from 'react-final-form';
import { IconPlusCircle } from 'components/atoms/icons/IconPlusCircle';
import {
  MaterialType,
  ProcessingMasterDetail,
  ProcessingMasterParams,
  UpdateRateType,
  VisibleColumn,
  WasteUnit,
} from 'util/processingMasterType';
import { getMaterialNamesBySite } from 'apis/operator_site/siteApi';
import {
  getProcessingMaster,
  updateProcessingMaster,
} from 'apis/processing_master/processingMasterApi';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { getSiteUserInfo } from 'apis/user_site/recycleApi';
import { uniqBy } from 'lodash';
import { toast } from 'react-toastify';
import ProcessingMasterTable from 'components/organisms/ProcessingMasterTable/ProcessingMasterTable';
import _debounce from 'lodash/debounce';
import { OptionItem } from 'components/molecules/SelectFieldMultipleChoise';
import { REGEX_OBJECT } from 'util/ConstantValues';

const DEFAULT_MAX_VISIBLE_COLUMN = 5;

const ProcessingMaster: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const [materialOptions, setMaterialOptions] = useState<SelectOption[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<VisibleColumn[]>([]);
  const [typeOptions, setTypeOptions] = useState<SelectOption[]>([]);
  const [editing, setEditing] = useState<boolean>(false);
  const [filter, setFilter] = useState<{ materialName: string }>({
    materialName: '',
  });
  const [tmpFilter, setTmpFilter] = useState<OptionItem[]>([]);
  const [processingMasterOriginalData, setProcessingMasterOriginalData] =
    useState<ProcessingMasterParams>({
      materialName: '',
      wasteUnits: [],
    });
  const [initialVisibleColumns, setInitialVisibleColumns] = useState<
    VisibleColumn[]
  >([]);
  const [processingMasterForm, setProcessingMasterForm] =
    useState<ProcessingMasterParams>({
      materialName: '',
      wasteUnits: [],
    });
  const [displayWasteUnits, setDisplayWasteUnits] = useState<WasteUnit[]>(
    processingMasterForm.wasteUnits
  );
  const [errors, setErrors] = useState<Record<string, string>>();
  const { setScrollToFirstError } = useScrollToFirstError({
    errors,
  });

  useEffect(() => {
    setProcessingMasterForm(
      JSON.parse(JSON.stringify(processingMasterOriginalData))
    );
    setVisibleColumns(JSON.parse(JSON.stringify(initialVisibleColumns)));
  }, [processingMasterOriginalData, editing, initialVisibleColumns]);

  const fetchMaterials = async (siteId: string) => {
    getMaterialNamesBySite({
      siteId,
      successCallback: (data) => {
        if (data.length > 0) {
          setMaterialOptions(
            data.map((name) => ({
              value: name,
              label: name,
            }))
          );

          if (!filter.materialName) {
            setFilter({ materialName: data[0] });
          }
        }
      },
    });
  };

  const fetchProcessingMaster = (materialName: string) => {
    getProcessingMaster({
      materialName,
      successCallback: (data) => {
        const sortedWasteUnits =
          data[0]?.wasteUnits?.sort(
            (a, b) =>
              (a.site?.name || '').localeCompare(b.site?.name || '') ||
              (a.name || '').localeCompare(b.name || '')
          ) ?? [];

        setProcessingMasterOriginalData({
          materialName,
          wasteUnits: sortedWasteUnits,
        });
        setInitialVisibleColumns(data[0]?.materialTypes ?? []);
      },
    });
  };

  const fetchMaterialTypes = () => {
    getSiteUserInfo(user.siteId || '', (data) => {
      setTypeOptions(
        data?.materialTypes?.map(({ id, name }) => ({
          value: id,
          label: name,
        })) ?? []
      );
    });
  };

  const initRateItem = (item: WasteUnit, type?: MaterialType) => {
    let rateItem = item.processingMasterDetails.filter(
      ({ materialType }) => materialType && materialType.id === type?.id
    )[0];

    if (!rateItem) {
      rateItem = { rate: '0', materialType: type };

      item.processingMasterDetails = [
        ...item.processingMasterDetails,
        rateItem,
      ];

      setDisplayWasteUnits((prev) => uniqBy([...prev, item]));
    }

    return rateItem;
  };

  const updateRateInput = (
    action?: UpdateRateType,
    materialType?: MaterialType,
    value?: string,
    wasteUnitId?: number | string
  ) => {
    if (value && !REGEX_OBJECT.max100With1Decimal?.test(String(value))) {
      return;
    }

    const newData = displayWasteUnits.map((item) => {
      const editItem = { ...item };
      const rateItems = editItem.processingMasterDetails;
      const setInput = () => {
        const rateItem = rateItems.filter(
          (el) => el.materialType?.id === materialType?.id
        )[0];
        rateItem['rate'] = String(value);
        return editItem;
      };

      switch (action) {
        case UpdateRateType.ChangeInput:
          if (editItem.id === wasteUnitId) {
            return setInput();
          }
          break;
        case UpdateRateType.AddColumn:
          initRateItem(item, materialType);
          return editItem;
        case UpdateRateType.DeleteColumn:
          editItem.processingMasterDetails = rateItems.filter(
            (el) => el.materialType?.id !== materialType?.id
          );
          return editItem;
        case UpdateRateType.SetColumnValue:
          return setInput();
      }

      return editItem;
    });

    setDisplayWasteUnits(newData);
    setProcessingMasterForm((prev) => ({
      ...prev,
      wasteUnits: updateWasteUnitsData('form'),
    }));

    _debounce(() => {
      validateRateInput(newData);
    }, 200)();
  };

  const validateRateInput = (data: WasteUnit[]) => {
    const validationResult = data
      .map(({ id, processingMasterDetails }) =>
        processingMasterDetails.map(({ rate, materialType }) => {
          if (!rate) {
            return { [`rate_${id}_${materialType?.id}`]: 'messages.M_007' };
          }

          const totalRate = processingMasterDetails.reduce((sum, item) => {
            const parsed = parseFloat(item.rate);
            return isNaN(parsed) ? sum : sum + parsed;
          }, 0);
          if (totalRate > 100) {
            return {
              [`rate_${id}_${materialType?.id}`]: 'messages.M_total_max',
            };
          }
        })
      )
      .flat()
      .filter((item) => item !== undefined);

    if (validationResult.length === 0) {
      setErrors(undefined);
    } else {
      setErrors(
        validationResult.reduce(
          (indexPath, message) => ({ ...indexPath, ...message }),
          {}
        )
      );
    }
  };

  const handleFilterByMaterial = (
    props: FormRenderProps,
    value: string | number
  ) => {
    setFilter({ materialName: String(value) });

    setEditing(false);
    props.form.mutators.setValue('materialName', value);
  };

  const handleChangeInput = (params: {
    wasteUnitId: number | string;
    materialType?: MaterialType;
    name?: string;
    value?: EditableTableCellValue | EditableTableCellValue[];
  }) => {
    const { name, wasteUnitId, materialType, value } = params;

    if (!name) {
      return;
    }

    setScrollToFirstError(false);

    updateRateInput(
      UpdateRateType.ChangeInput,
      materialType,
      String(value),
      wasteUnitId
    );
  };

  const handleSubmit = () => {
    setScrollToFirstError(true);

    const updatingData = updateWasteUnitsData('payload', true);

    if (!updatingData.length) {
      setEditing(false);

      return;
    }

    updateProcessingMaster(
      {
        ...processingMasterForm,
        wasteUnits: updatingData,
      },
      () => {
        toast.success(t('messages.M_011'));
        setEditing(false);
        fetchProcessingMaster(processingMasterOriginalData.materialName);
      }
    );
  };

  const updateWasteUnitsData = (
    type: 'payload' | 'form',
    extractFromFilter?: boolean
  ) => {
    const displayWasteUnitIds = displayWasteUnits.map((item) => item.id);

    return processingMasterForm.wasteUnits.map(
      ({ id, processingMasterDetails, name, site }) => {
        const newData = displayWasteUnits.filter((item) => item.id === id)[0];
        let rateItems: ProcessingMasterDetail[] = [];

        if (newData) {
          rateItems = newData.processingMasterDetails;
        } else if (processingMasterDetails.length) {
          rateItems = processingMasterDetails;
        } else {
          rateItems = visibleColumns.map((option) => ({
            rate: '0',
            materialTypeId: option.id,
            materialType: {
              id: option.id,
              name: option.name,
            },
          }));
        }

        return {
          id,
          ...(type === 'form' && { name }),
          ...(type === 'form' && { site }),
          processingMasterDetails: rateItems.map(({ rate, materialType }) => ({
            rate:
              !extractFromFilter || displayWasteUnitIds.includes(id)
                ? rate.replace(/\.$/, '')
                : processingMasterOriginalData.wasteUnits
                    .filter((wu) => wu.id === id)[0]
                    .processingMasterDetails.filter(
                      (md) => md.materialType?.id === materialType?.id
                    )[0]?.rate || '0',
            ...(type === 'payload' && { materialTypeId: materialType?.id }),
            ...(type === 'form' && {
              materialType: {
                id: materialType?.id || 0,
                name: materialType?.name || '',
              },
            }),
          })),
        };
      }
    );
  };

  const handleCancel = () => {
    setEditing(false);
  };

  const handleDeleteColumn = (visibleColumn: VisibleColumn) => {
    const newColumns = visibleColumns.filter(
      (col) => col.id !== visibleColumn.id
    );

    updateRateInput(UpdateRateType.DeleteColumn, visibleColumn);
    setVisibleColumns(newColumns);
  };

  const handleAddColumn = () => {
    updateRateInput(UpdateRateType.AddColumn, remainingOptions[0]);
    setVisibleColumns((prev) => [...prev, remainingOptions[0]]);
  };

  const remainingOptions = useMemo(() => {
    return typeOptions
      .filter(({ value }) => !visibleColumns.some((col) => col.id === value))
      .map(({ value, label }) => ({ id: Number(value), name: label }));
  }, [visibleColumns]);

  const filterRateItemsByVisibleColumns = (items: WasteUnit[]) => {
    return items.map((item) => ({
      ...item,
      processingMasterDetails: item.processingMasterDetails.filter(
        ({ materialType }) =>
          visibleColumns.some((el) => el.id === materialType?.id)
      ),
    }));
  };

  useEffect(() => {
    fetchMaterials(String(user.siteId));
    fetchMaterialTypes();
  }, []);

  useEffect(() => {
    if (filter.materialName) {
      fetchProcessingMaster(filter.materialName);
      setErrors(undefined);
    }
  }, [filter]);

  useEffect(() => {
    if (editing) {
      visibleColumns.length === 0 && setVisibleColumns([remainingOptions[0]]);
      return;
    }

    setErrors(undefined);
  }, [editing]);

  useEffect(() => {
    const newData =
      tmpFilter.length > 0
        ? processingMasterForm.wasteUnits.filter(({ name }) => {
            const dataName = name || t('processing_master.item_name_common');
            return tmpFilter?.some((item) => item.name === dataName);
          })
        : processingMasterForm.wasteUnits;

    setDisplayWasteUnits(filterRateItemsByVisibleColumns(newData));
  }, [tmpFilter, processingMasterForm]);

  return (
    <PrimaryTemplate h1={t('nav.processing_master')}>
      <div className={styles.processingMaster}>
        <div className={styles.bar}>
          <Form
            onSubmit={() => {}}
            mutators={{
              setValue: ([field, value], state, { changeValue }) => {
                changeValue(state, field, () => value);
              },
            }}
          >
            {(props) => {
              return (
                <form className={styles.filterForm}>
                  <div>{t('processing_master.label_material')}</div>
                  <SelectField
                    name="materialName"
                    value={
                      props.values.materialName || materialOptions[0]?.label
                    }
                    options={materialOptions}
                    onChange={(value) => handleFilterByMaterial(props, value)}
                    isDarkmode
                  />
                </form>
              );
            }}
          </Form>

          {editing && (
            <button
              className={styles.addColumnButton}
              disabled={
                visibleColumns.length >=
                Math.min(typeOptions.length, DEFAULT_MAX_VISIBLE_COLUMN)
              }
              onClick={handleAddColumn}
            >
              <div className={styles.iconAdd}>
                <IconPlusCircle />
              </div>
              <span>{t('processing_master.add_column')}</span>
            </button>
          )}
        </div>

        <ProcessingMasterTable
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          remainingOptions={remainingOptions}
          processingMasterForm={processingMasterForm}
          handleChangeInput={handleChangeInput}
          handleDeleteColumn={handleDeleteColumn}
          initRateItem={initRateItem}
          updateRateInput={updateRateInput}
          filter={tmpFilter}
          setFilter={setTmpFilter}
          displayWasteUnits={displayWasteUnits}
          editing={editing}
          errors={errors}
        />

        <BottomField>
          <div className={styles.bottomBtn}>
            {editing && (
              <button
                className={styles.btnCancel}
                type="button"
                onClick={handleCancel}
              >
                {t('common.button.cancel')}
              </button>
            )}

            <button
              className={editing ? styles.btnSave : styles.btnEdit}
              type="button"
              onClick={editing ? handleSubmit : () => setEditing(true)}
              disabled={
                displayWasteUnits.length === 0 ||
                (editing && errors && Object.values(errors).some((err) => err))
              }
            >
              {editing ? t('common.button.save') : t('common.edit')}
            </button>
          </div>
        </BottomField>
      </div>
    </PrimaryTemplate>
  );
};

export default ProcessingMaster;
